<script setup>
import VueDatePicker from "@vuepic/vue-datepicker"
import { es } from "date-fns/locale"
import { useDuracionInput } from "@/composables/useDuracionInput"

// Mismo formulario que VueloManualModal.vue (cotizador automático) para
// capturar un tramo de vuelo: aerolínea, hora de salida/llegada, duración,
// tipo (directo/con escalas) y, si aplica, las escalas (ciudad + duración).
defineProps({
  titulo: { type: String, required: true },
  mostrarDiaLlegada: { type: Boolean, default: true },
  mostrarFecha: { type: Boolean, default: false },
  fechaMin: { type: String, default: null },
  fechaMax: { type: String, default: null }
})

// v-model:vuelo — objeto mutable compartido con el padre (aerolinea, salida,
// llegada, duracion, tipo, diasExtra, escalas, y en el tramo intermedio
// también fecha).
const vuelo = defineModel("vuelo", { required: true })

const { validarYFormatearDuracion, bloquearDuracion } = useDuracionInput()

function actualizarDuracion(value) {
  vuelo.value.duracion = validarYFormatearDuracion(value)
}

function actualizarNumeroEscalas(cantidad) {
  const total = Number(cantidad)
  if (!vuelo.value.escalas) vuelo.value.escalas = []
  while (vuelo.value.escalas.length < total) vuelo.value.escalas.push({ duracion: "", nombre: "" })
  while (vuelo.value.escalas.length > total) vuelo.value.escalas.pop()
}
</script>

<template>
  <div class="row g-2 mb-3">
    <div class="col-12"><strong class="small">{{ titulo }}</strong></div>

    <div v-if="mostrarFecha" class="col-6 col-md-2">
      <label class="form-label small mb-0">Fecha</label>
      <VueDatePicker v-model="vuelo.fecha" :format-locale="es" locale="es" model-type="yyyy-MM-dd"
        format="yyyy-MM-dd" auto-apply :enable-time-picker="false" :clearable="false"
        :min-date="fechaMin" :max-date="fechaMax" />
    </div>

    <div class="col-6 col-md-2">
      <label class="form-label small mb-0">Aerolínea</label>
      <input type="text" class="form-control" v-model="vuelo.aerolinea">
    </div>

    <div class="col-6 col-md-2">
      <label class="form-label small mb-0">Salida</label>
      <VueDatePicker v-model="vuelo.salida" time-picker is-24 format="HH:mm" model-type="HH:mm"
        auto-apply :clearable="false" />
    </div>

    <div class="col-6 col-md-2">
      <label class="form-label small mb-0">Llegada</label>
      <VueDatePicker v-model="vuelo.llegada" time-picker is-24 format="HH:mm" model-type="HH:mm"
        auto-apply :clearable="false" />
    </div>

    <div v-if="mostrarDiaLlegada" class="col-6 col-md-2">
      <label class="form-label small mb-0">Día llegada</label>
      <select v-model.number="vuelo.diasExtra" class="form-select">
        <option :value="0">Mismo día</option>
        <option :value="1">+1 día</option>
        <option :value="2">+2 días</option>
      </select>
    </div>

    <div class="col-6 col-md-2">
      <label class="form-label small mb-0">Duración</label>
      <input :value="vuelo.duracion" @keydown="bloquearDuracion" @input="actualizarDuracion($event.target.value)"
        class="form-control" placeholder="Ej: 5h 30m">
    </div>

    <div class="col-6 col-md-2">
      <label class="form-label small mb-0">Tipo</label>
      <select v-model="vuelo.tipo" class="form-select">
        <option value="directo">Directo</option>
        <option value="con escalas">Con escala</option>
      </select>
    </div>

    <div v-if="vuelo.tipo === 'con escalas'" class="col-12">
      <div class="row g-2 mt-1">
        <div class="col-md-4">
          <label class="form-label small mb-0">Número de escalas</label>
          <input type="number" min="1" max="5" class="form-control" :value="vuelo.escalas?.length || 1"
            @input="actualizarNumeroEscalas($event.target.value)">
        </div>
      </div>

      <div v-for="(escala, i) in vuelo.escalas" :key="i" class="row g-2 mt-1">
        <div class="col-md-4">
          <label class="form-label small mb-0">Tiempo escala</label>
          <input :value="escala.duracion" @keydown="bloquearDuracion"
            @input="escala.duracion = validarYFormatearDuracion($event.target.value)" class="form-control"
            placeholder="Ej: 2h 30m">
        </div>
        <div class="col-md-8">
          <label class="form-label small mb-0">Ciudad de la escala</label>
          <input type="text" class="form-control" placeholder="Ej: Aeropuerto El Dorado" v-model="escala.nombre">
        </div>
      </div>
    </div>
  </div>
</template>
